import { useState } from "react";
import type { CatalogCategoryDTO } from "../../api/types.js";
import { messages } from "../../i18n/messages.js";

interface CategoryChipsProps {
  collections: CatalogCategoryDTO[];
  primary: CatalogCategoryDTO[];
  overflow: CatalogCategoryDTO[];
  activeCategory: string | null;
  activeCollection: string | null;
  onSelectCategory: (categoryId: string | null) => void;
  onSelectCollection: (collectionId: string | null) => void;
  onOpenAllCategories?: (() => void) | undefined;
  showMoreLabel?: string;
}

function chipClass(active: boolean): string {
  return active
    ? "shrink-0 rounded-full bg-[var(--tg-theme-button-color,#3390ec)] px-3 py-2 text-xs font-medium text-[var(--tg-theme-button-text-color,#fff)]"
    : "shrink-0 rounded-full border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] px-3 py-2 text-xs font-medium";
}

export function CategoryChips({
  collections,
  primary,
  overflow,
  activeCategory,
  activeCollection,
  onSelectCategory,
  onSelectCollection,
  onOpenAllCategories,
  showMoreLabel = messages.catalog.showMoreCategories,
}: CategoryChipsProps) {
  const [expanded, setExpanded] = useState(false);
  const visibleOverflow = expanded ? overflow : [];

  function isCollectionActive(id: string): boolean {
    return activeCollection === id && !activeCategory;
  }

  function isCategoryActive(id: string): boolean {
    return activeCategory === id && !activeCollection;
  }

  return (
    <div className="-mx-1 flex flex-wrap gap-2 px-1 pb-1">
      {collections.map((chip) => {
        const active = isCollectionActive(chip.id);
        return (
          <button
            key={`collection-${chip.id}`}
            type="button"
            onClick={() => {
              onSelectCategory(null);
              onSelectCollection(chip.id);
            }}
            className={chipClass(active)}
          >
            {chip.label}
          </button>
        );
      })}

      {primary.map((chip) => {
        const active = isCategoryActive(chip.id);
        return (
          <button
            key={`category-${chip.id}`}
            type="button"
            onClick={() => {
              // FUNCTIONALITY-092: first click always selects (no toggle-off). Clear via All/reset.
              onSelectCollection(null);
              onSelectCategory(chip.id);
            }}
            className={chipClass(active)}
          >
            {chip.label}
          </button>
        );
      })}

      {visibleOverflow.map((chip) => {
        const active = isCategoryActive(chip.id);
        return (
          <button
            key={`overflow-${chip.id}`}
            type="button"
            onClick={() => {
              onSelectCollection(null);
              onSelectCategory(chip.id);
            }}
            className={chipClass(active)}
          >
            {chip.label}
          </button>
        );
      })}

      {!expanded && overflow.length > 0 ? (
        <button type="button" onClick={() => setExpanded(true)} className={chipClass(false)}>
          {showMoreLabel}
        </button>
      ) : null}

      {onOpenAllCategories ? (
        <button type="button" onClick={onOpenAllCategories} className={chipClass(false)}>
          {messages.catalog.allCategories}
        </button>
      ) : null}
    </div>
  );
}
